/**
 * Baustein für Aufgaben, die nach bestimmten Funktionen in Formeln suchen.
 *
 * Die meisten Aufgaben des Moduls „Weitere Funktionen“ haben dieselbe Form:
 * Irgendwo in der Arbeitsmappe soll eine Formel stehen, die eine oder mehrere
 * Funktionen verwendet – und ihr Ergebnis soll kein Fehlerwert sein.
 */

import { criterion, fail, fromCriteria } from "@/core/check";
import type { CheckCriterion, CheckResult, TaskCheck } from "@/core/types";
import { nutztEineFunktion } from "@/office/excel/formeln";
import { type CellInfo, formulaCells, isError, readWorkbook } from "@/office/excel/workbook";
import { nichtPruefbar } from "./schnittstelle";

export interface FormelKriterium {
  /** Text des Kriteriums in der Rückmeldung. */
  label: string;
  /** Funktionsschlüssel aus `formeln.ts`; eine davon genügt. */
  funktionen?: string[];
  /** Zusätzliche Bedingung an die Formel. */
  test?: (formel: string) => boolean;
}

function erfuellt(zelle: CellInfo, kriterium: FormelKriterium): boolean {
  const mitFunktion = kriterium.funktionen ? nutztEineFunktion(zelle.formula, kriterium.funktionen) : true;
  return mitFunktion && (kriterium.test ? kriterium.test(zelle.formula) : true);
}

/**
 * Prüft, ob für jedes Kriterium mindestens eine Formel existiert. Für die
 * Formeln des ersten Kriteriums wird zusätzlich verlangt, dass sie keinen
 * Fehlerwert zeigen.
 *
 * @param gemeinsam `true`, wenn eine einzige Formel alle Kriterien zugleich
 *                  erfüllen muss (etwa INDEX und VERGLEICH in derselben Formel).
 */
export function formelAufgabe(
  kriterien: FormelKriterium[],
  texte: { success: string; failure: string; hint?: string },
  gemeinsam = false,
): TaskCheck {
  return async (): Promise<CheckResult> => {
    const mappe = await readWorkbook();
    if (!mappe.features.cells) {
      return nichtPruefbar(
        "Die Formeln der Zellen",
        "1.4",
        "Sieh in der Bearbeitungsleiste nach, ob dort die verlangte Funktion steht.",
        true,
      );
    }
    const formeln = formulaCells(mappe);
    if (formeln.length === 0) {
      return fail("Es steht noch keine Formel in der Arbeitsmappe.", texte.hint);
    }

    const ergebnis: CheckCriterion[] = [];
    let treffer: CellInfo[];
    if (gemeinsam) {
      treffer = formeln.filter((zelle) => kriterien.every((kriterium) => erfuellt(zelle, kriterium)));
      ergebnis.push(
        criterion(
          `${kriterien.map((kriterium) => kriterium.label).join(" und ")} in einer Formel` +
            (treffer[0] ? ` (etwa ${treffer[0].address}: ${treffer[0].formula})` : ""),
          treffer.length > 0,
        ),
      );
    } else {
      treffer = formeln.filter((zelle) => erfuellt(zelle, kriterien[0]));
      for (const kriterium of kriterien) {
        const passend = formeln.filter((zelle) => erfuellt(zelle, kriterium));
        ergebnis.push(
          criterion(
            passend[0] ? `${kriterium.label} (etwa ${passend[0].address}: ${passend[0].formula})` : kriterium.label,
            passend.length > 0,
          ),
        );
      }
    }

    const fehlerhaft = treffer.filter(isError);
    if (treffer.length > 0) {
      ergebnis.push(
        criterion(
          fehlerhaft.length === 0
            ? "Das Ergebnis ist kein Fehlerwert"
            : `Fehlerwert in ${fehlerhaft[0].address}: ${fehlerhaft[0].text}`,
          fehlerhaft.length === 0,
        ),
      );
    }
    return fromCriteria(ergebnis, texte);
  };
}
